import React, { useState, useRef, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import apiService from "../../services/api";
import { parsePillar, getStemColor_nobg, getHiddenStems, getBranchColor_nobg } from "../_helpers"
import { calculateBaziAnalysis, calculatePillarAnalysis } from "./_helper_bazi";
import { Solar } from "../../services/lunar.js";
import { Loader, Clock, ArrowLeft, ArrowRight, Info } from "lucide-react";
import BZChart from "./BZChart";
import BaziPublish from "./BaziPublish";

const BaziDetail = ({ currentView, setCurrentView, isLoggedIn, onLogout }) => {
  const location = useLocation();
  const navigate = useNavigate();

  // 从 location.state 获取传入的 bzChart 数据（普通对象）
  // 兼容旧结构 (直接是 chartData) 和新结构 ({ result: chartData, form: formData })
  const stateData = location.state || null;
  const bzChart = stateData?.result || stateData;

  // 使用 BZChart.getFullBZChart 获取包含藏干的完整数据
  const fullChart = bzChart ? BZChart.getFullBZChart(bzChart) : null;

  // State 管理 - 所有 Hooks 必须放在组件顶部
  const [result, setResult] = useState(fullChart);
  const initialForm = fullChart ? {
    mingzhu_name: fullChart.mingzhu_name,
    gender: fullChart.mingzhu_gender,
    shudi: fullChart.shudi || "",
    zhanti: fullChart.zhanti || "",
    zhanti_type: fullChart.zhanti_type || "",
    year: fullChart.year,
    month: fullChart.month,
    day: fullChart.day,
    hour: fullChart.hour,
    chart_id: fullChart.chart_id || fullChart.id
  } : null;
  const [form, setForm] = useState(initialForm);
  const [ownerNote, setOwnerNote] = useState(fullChart?.owner_feedback || "");
  const [masterNote, setMasterNote] = useState(fullChart?.master_record || "");
  const [zhanti, setZhanti] = useState(fullChart?.zhanti || "");
  const [zhantiType, setZhantiType] = useState(fullChart?.zhanti_type || "");
  const [detailTab, setDetailTab] = useState("pillar");

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [saveSuccess, setSaveSuccess] = useState("");
  const [selectedTab, setSelectedTab] = useState("bazi");

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, []);

  // 发布相关状态
  const [showPublishModal, setShowPublishModal] = useState(false);

  // 大运流年相关状态
  const [loadingDaYun, setLoadingDaYun] = useState(false);
  const [daYunData, setDaYunData] = useState(null);
  const [daYunError, setDaYunError] = useState("");
  const [selectedDayunIndex, setSelectedDayunIndex] = useState(0);
  const [selectedLiunianIndex, setSelectedLiunianIndex] = useState(0);

  // 当切换到大运流年 tab 时加载数据
  useEffect(() => {
    if (selectedTab === "dayun" && bzChart && !daYunData && !loadingDaYun) {
      loadDaYunData();
    }
  }, [selectedTab, bzChart]);

  // 如果没有收到 bzChart，提示用户返回
  if (!bzChart) {
    return (
      <div className="p-6">
        <div className="mb-4 text-gray-700">未收到完整的命盘信息，请返回计算页重新操作。</div>
        <button
          className="px-4 py-2 bg-indigo-600 text-white rounded"
          onClick={() => navigate(-1)}
        >
          返回
        </button>
      </div>
    );
  }

  if (!fullChart) {
    return (
      <div className="p-6">
        <div className="mb-4 text-red-600">命盘数据格式错误，无法解析。</div>
        <button
          className="px-4 py-2 bg-indigo-600 text-white rounded"
          onClick={() => navigate(-1)}
        >
          返回
        </button>
      </div>
    );
  }

  const baziAnalysis = (result?.year && !isNaN(result.year)) ? calculateBaziAnalysis(result.year, result?.month, result?.day, result?.hour) : {
    year: { ganShiShen: '', zhiShiShen: [], starLuck: '', diShi: '', xunKong: '', naYin: '' },
    month: { ganShiShen: '', zhiShiShen: [], starLuck: '', diShi: '', xunKong: '', naYin: '' },
    day: { ganShiShen: '', zhiShiShen: [], starLuck: '', diShi: '', xunKong: '', naYin: '' },
    hour: { ganShiShen: '', zhiShiShen: [], starLuck: '', diShi: '', xunKong: '', naYin: '' }
  };

  const displayName = (result?.mingzhu_name || "").trim();
  const displayGender = (() => {
    const g = result?.mingzhu_gender || "";
    if (!g) return "";
    const low = String(g).toLowerCase();
    if (["male", "m", "男", "man"].includes(low)) return "男";
    if (["female", "f", "女", "woman"].includes(low)) return "女";
    return g;
  })();

  const lunarBirthText = (() => {
    const y = Number(form?.year);
    const m = Number(form?.month);
    const d = Number(form?.day);
    const h = Number(form?.hour);
    if (!y || !m || !d) return "";

    try {
      const solar = Solar.fromYmdHms(y, m, d, Number.isNaN(h) ? 0 : h, 0, 0);
      const lunar = solar.getLunar();
      return `${lunar.getYearInChinese()}年${lunar.getMonthInChinese()}月${lunar.getDayInChinese()}`;
    } catch (e) {
      return "";
    }
  })();

  const loadDaYunData = async () => {
    setLoadingDaYun(true);
    setDaYunError("");
    try {
      const data = await BZChart.calculateDaYun(bzChart);
      setDaYunData(data);

      // 设置默认选中项
      if (data.daYunArr && data.daYunArr.length > 0) {
        // 找到当前大运的索引
        const currentIndex = data.daYunArr.findIndex(
          (p) => p === data.currentPeriod ||
            Number(p.period_index) === Number(data.currentPeriod?.period_index)
        );
        setSelectedDayunIndex(currentIndex !== -1 ? currentIndex : 0);

        // 找到当前流年的索引
        const selectedDayun = data.daYunArr[currentIndex !== -1 ? currentIndex : 0];
        const liunianList = Array.isArray(selectedDayun?.liunian) ? selectedDayun.liunian : [];
        if (data.liunianForCurrentYear && liunianList.length > 0) {
          const liIdx = liunianList.findIndex(
            (ln) => Number(ln.year) === Number(data.liunianForCurrentYear.year)
          );
          setSelectedLiunianIndex(liIdx !== -1 ? liIdx : 0);
        }
      }
    } catch (err) {
      console.error("加载大运流年失败:", err);
      setDaYunError(err.message || "加载大运流年数据失败");
    } finally {
      setLoadingDaYun(false);
    }
  };
  // 提取藏干的辅助函数,支出传入“pillar“或者“branch”字段的对象。而不需要从后台获取
  const pickHidden = (obj) => {
    if (!obj) return [];
    return (
      getHiddenStems(obj.pillar) ||
      []
    );
  };

  const renderHiddenStems = (arr) => {
    if (!arr || !Array.isArray(arr) || arr.length === 0) return null;
    return (
      <div className="mt-1 sm:mt-2">
        <div className="flex items-center justify-center gap-0.5 sm:gap-2 flex-wrap">
          {arr.map((h, idx) => (
            <span key={idx} className={`inline-block px-1 py-0.5 rounded text-[10px] sm:text-sm font-medium ${getStemColor_nobg(h)}`}>
              {h}
            </span>
          ))}
        </div>
      </div>
    );
  };



  // 处理发布按钮点击
  const handlePublish = async () => {
    // 先执行保存操作
    if (!apiService.isAuthenticated()) {
      const returnInfo = {
        path: location.pathname,
        search: location.search,
        state: location.state
      };
      sessionStorage.setItem("post_login_return", JSON.stringify(returnInfo));
      alert('请先登录，登录后将自动返回当前页面');
      return;
    }

    if (!result) {
      alert("无八字结果可保存");
      return;
    }

    // 获取当前表单值
    const ownerVal = ownerNote;
    const masterVal = masterNote;

    // 优先使用 state 中的 ID，其次是 fullChart 中的 ID
    const chartId = form?.chart_id || result?.id || null;

    const payload = {
      ...form,
      ...result,
      mingzhu_name: form?.mingzhu_name || "",
      zhanti: zhanti,
      zhanti_type: zhantiType,
      ownerNote: ownerVal,
      owner_feedback: ownerVal,
      masterNote: masterVal,
      master_record: masterVal,
      mingzhu_gender: form?.gender || "",
      chart_id: chartId
    };

    try {
      const res = await apiService.saveChart(payload);

      if (res.success) {
        // 保存成功后更新 id，再打开发布弹窗
        const newChartId = res.data?.chart_id || res.data?.id;
        if (newChartId) {
          setForm(prev => ({ ...prev, chart_id: newChartId }));
          setResult(prev => ({ ...prev, id: newChartId }));
        }
        setShowPublishModal(true);
      } else {
        alert("保存失败: " + res.error);
      }
    } catch (e) {
      alert("保存失败: " + e.message);
    }
  };

  const handleSaveBazi = async () => {
    if (!apiService.isAuthenticated()) { // 恢复为 apiService.isAuthenticated()
      // 未登录时，保存当前状态以便登录后恢复
      const returnInfo = {
        path: location.pathname,
        search: location.search,
        state: location.state
      };
      sessionStorage.setItem("post_login_return", JSON.stringify(returnInfo));

      alert('请先登录，登录后将自动返回当前页面');
      navigate('/login');
      return;
    }

    if (!result) {
      alert("无八字结果可保存");
      return;
    }
    setSaving(true);
    try {
      const ownerVal = ownerNote;
      const masterVal = masterNote;

      // 优先使用 state 中的 ID，其次是 fullChart 中的 ID
      const chartId = form?.chart_id || result?.id || null;

      const payload = {
        ...form,
        ...result,
        mingzhu_name: form?.mingzhu_name || "",
        zhanti: zhanti,
        zhanti_type: zhantiType,
        ownerNote: ownerVal,
        owner_feedback: ownerVal,
        masterNote: masterVal,
        master_record: masterVal,
        mingzhu_gender: form?.gender || "",
        chart_id: chartId
      };

      const res = await apiService.saveChart(payload);

      if (res.success) {
        alert(chartId ? "更新成功" : "保存成功");
        const newChartId = res.data?.chart_id || res.data?.id;
        if (newChartId) {
          setForm(prev => ({ ...prev, chart_id: newChartId }));
          setResult(prev => ({ ...prev, id: newChartId }));
        }
      } else {
        alert("保存失败: " + res.error);
      }
    } catch (e) {
      alert("保存失败: " + e.message);
    } finally {
      setSaving(false);
    }
  };

  // 发布弹窗提交
  const handlePublishSubmit = async (formData) => {
    console.log('handlePublishSubmit received formData:', formData);
    try {
      // 准备完整的chart数据，让后台判断是否需要保存
      const ownerVal = ownerNote;
      const masterVal = masterNote;

      const chartId = form?.chart_id || result?.id;

      const fullFormData = {
        ...formData,
        chartData: {
          ...form,
          ...result,
          mingzhu_name: form?.mingzhu_name || "",
          zhanti: zhanti,
          zhanti_type: zhantiType,
          ownerNote: ownerVal,
          owner_feedback: ownerVal,
          masterNote: masterVal,
          master_record: masterVal,
          mingzhu_gender: form?.gender || "",
          chart_id: chartId
        }
      };

      const res = await apiService.publishBaziChart(fullFormData);
      console.log('apiService.publishBaziChart response:', res);
      if (res.success) {
        // 如果后台返回了新的chart_id，更新本地状态
        if (res.data?.chart_id || res.data?.id) {
          setForm(prev => ({ ...prev, chart_id: res.data.chart_id || res.data.id }));
          setResult(prev => ({ ...prev, id: res.data.chart_id || res.data.id }));
        }

        alert('发布成功！');
        setShowPublishModal(false);
      } else {
        alert('发布失败: ' + (res.error || '未知错误'));
      }
    } catch (error) {
      alert('发布失败: ' + (error?.message || '未知错误'));
    }
  };

  const renderTabs = () => {
    return (
      <div className="mb-4 flex items-center gap-2 overflow-x-auto pb-1">
        <button
          className={`flex-shrink-0 px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${selectedTab === "bazi" ? "bg-indigo-600 text-white shadow-md" : "bg-white text-gray-600 border border-gray-200"}`}
          onClick={() => setSelectedTab("bazi")}
        >
          基础命盘
        </button>
        <button
          className={`flex-shrink-0 px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${selectedTab === "dayun" ? "bg-indigo-600 text-white shadow-md" : "bg-white text-gray-600 border border-gray-200"}`}
          onClick={() => setSelectedTab("dayun")}
        >
          专业细盘
        </button>
      </div>
    );
  };

  // 获取当前选中的大运和流年数据
  const getCurrentDaYunData = () => {
    if (!daYunData || !daYunData.daYunArr) return null;

    const currentPeriod = daYunData.daYunArr[selectedDayunIndex] || daYunData.currentPeriod;
    const currentPeriodHiddenStems = currentPeriod ? pickHidden(currentPeriod) : daYunData.currentPeriodHiddenStems || [];

    const liunianList = Array.isArray(currentPeriod?.liunian) ? currentPeriod.liunian : [];
    const liunianForCurrentYear = liunianList[selectedLiunianIndex] || daYunData.liunianForCurrentYear;
    const liunianCurrentYearHiddenStems = liunianForCurrentYear ? pickHidden(liunianForCurrentYear) : daYunData.liunianCurrentYearHiddenStems || [];

    return {
      currentPeriod,
      currentPeriodHiddenStems,
      liunianForCurrentYear,
      liunianCurrentYearHiddenStems,
    };
  };

  return (
    <>

      <div className="w-full max-w-5xl mx-auto p-3 sm:p-6">
        {renderTabs()}

        {selectedTab === "dayun" && (
          <>
            {loadingDaYun && (
              <div className="p-6 text-center">
                <Loader className="animate-spin inline-block mr-2" />
                加载大运流年数据...
              </div>
            )}

            {daYunError && (
              <div className="p-6 text-red-600">
                {daYunError}
                <button
                  onClick={loadDaYunData}
                  className="ml-4 px-3 py-1 bg-indigo-600 text-white rounded"
                >
                  重试
                </button>
              </div>
            )}

            {!loadingDaYun && !daYunError && daYunData && (
              <>
                {/* Panel E：大运组框 */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-2 sm:p-4 mb-1 sm:mb-3">
                    {/* 起运/交运 */} {/* 启运折算 */}
                  <div className="flex flex-col items-start justify-center p-2 sm:p-3">
                      <span className="text-xs text-gray-500 mb-1">起运：{daYunData.meta?.qiyun_des }</span>
                      <span className="text-xs text-gray-500 mb-1">折算：{daYunData.meta?.equivalent ?? "—"}</span>                  
                    </div>
                </div>

                <div className="flex flex-col md:flex-row gap-4 sm:gap-6 mb-4 sm:mb-6">
                  {/* Panel F：当前大运/流年与四柱 */}
                  <div className="flex-1 bg-white rounded-xl border border-gray-300 shadow-lg overflow-hidden p-3 sm:p-6 w-full min-w-0">
                    <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-3">当前流年</h3>
                    <div className="w-full overflow-x-auto pb-2">
                      {(() => {
                        const current = getCurrentDaYunData();
                        const dayGan = result?.day_pillar ? parsePillar(result.day_pillar)[0] : null;

                        const pillars = [
                          { title: "大运", pillar: current?.currentPeriod?.pillar || current?.currentPeriod?.pillar_name, type: "dayun", color: "text-indigo-600" },
                          { title: "流年", pillar: current?.liunianForCurrentYear?.pillar, type: "liunian", color: "text-blue-600" },
                          { title: "年柱", pillar: result?.year_pillar, type: "year", color: "text-indigo-600" },
                          { title: "月柱", pillar: result?.month_pillar, type: "month", color: "text-purple-600" },
                          { title: "日柱", pillar: result?.day_pillar, type: "day", color: "text-pink-600" },
                          { title: "时柱", pillar: result?.hour_pillar, type: "hour", color: "text-blue-600" }
                        ];

                        // Prepare analysis data
                        const analysisData = pillars.map(p => {
                          if (!p.pillar) return null;
                          if (["year", "month", "day", "hour"].includes(p.type)) {
                            return baziAnalysis[p.type];
                          } else {
                            return calculatePillarAnalysis(dayGan, p.pillar);
                          }
                        });

                        return (
                          <div className="grid grid-cols-7 gap-0 text-center items-center w-full">
                            {/* Row 1: Title */}
                            <div className="text-[10px] sm:text-xs font-bold text-gray-500 pb-1">日期</div>
                            {pillars.map((p, i) => (
                              <div key={`t-${i}`} className={`text-[10px] sm:text-xs font-semibold ${p.color} pb-1`}>{p.title}</div>
                            ))}

                            {/* Row 2: Main Star */}
                            <div className="text-xs sm:text-sm font-bold text-gray-500 py-1">主星</div>
                            {analysisData.map((d, i) => (
                              <div key={`ms-${i}`} className="text-xs sm:text-sm text-gray-800 py-1 min-h-[24px]">
                                {d?.ganShiShen || ""}
                              </div>
                            ))}

                            {/* Row 3: Stem */}
                            <div className="text-xs sm:text-sm font-bold text-gray-500 py-1">天干</div>
                            {pillars.map((p, i) => {
                              const [stem] = parsePillar(p.pillar);
                              return (
                                <div key={`s-${i}`} className={`text-lg sm:text-xl font-bold ${stem ? getStemColor_nobg(stem) : ""}`}>
                                  {stem || ""}
                                </div>
                              );
                            })}

                            {/* Row 4: Branch */}
                            <div className="text-xs sm:text-sm font-bold text-gray-500 py-1">地支</div>
                            {pillars.map((p, i) => {
                              const [, branch] = parsePillar(p.pillar);
                              return (
                                <div key={`b-${i}`} className={`text-lg sm:text-xl font-bold ${branch ? getBranchColor_nobg(branch) : ""}`}>
                                  {branch || ""}
                                </div>
                              );
                            })}

                            {/* Row 5: Hidden Stems & Sub Star */}
                            <div className="text-xs sm:text-sm font-bold text-gray-500 py-1 self-start mt-2">藏干</div>
                            {pillars.map((p, i) => {
                              const hidden = getHiddenStems(p.pillar);
                              const subStars = analysisData[i]?.zhiShiShen || [];
                              return (
                                <div key={`hs-${i}`} className="flex flex-col items-center gap-0 mt-2 self-start min-h-[50px]">
                                  {/* 藏干在第一行 */}
                                  <div className="flex items-center gap-1 flex-wrap mb-1">
                                    {hidden && hidden.map((h, idx) => (
                                      <span key={`h-${idx}`} className={`text-xs font-medium leading-tight ${getStemColor_nobg(h)}`}>
                                        {h}
                                      </span>
                                    ))}
                                  </div>
                                  {/* 副星在第二行 */}
                                  <div className="flex items-center gap-1 flex-wrap">
                                    {subStars.map((s, idx) => (
                                      <span key={`s-${idx}`} className="text-xs text-gray-600 leading-tight">
                                        {s}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              );
                            })}

                            {/* Row 7: Star Luck */}
                            <div className="text-xs sm:text-sm font-bold text-gray-500 py-1">星运</div>
                            {analysisData.map((d, i) => (
                              <div key={`sl-${i}`} className="text-xs sm:text-sm text-gray-800 py-1 min-h-[24px]">
                                {d?.starLuck || ""}
                              </div>
                            ))}

                            {/* Row 8: Self Sit */}
                            <div className="text-xs sm:text-sm font-bold text-gray-500 py-1">自坐</div>
                            {analysisData.map((d, i) => (
                              <div key={`sit-${i}`} className="text-xs sm:text-sm text-gray-800 py-1 min-h-[24px]">
                                {d?.diShi || ""}
                              </div>
                            ))}

                            {/* Row 9: Void */}
                            <div className="text-xs sm:text-sm font-bold text-gray-500 py-1">空亡</div>
                            {analysisData.map((d, i) => (
                              <div key={`kw-${i}`} className="text-xs sm:text-sm text-gray-800 py-1 min-h-[24px]">
                                {d?.xunKong || ""}
                              </div>
                            ))}

                            {/* Row 10: Na Yin */}
                            <div className="text-xs sm:text-sm font-bold text-gray-500 py-1">纳音</div>
                            {analysisData.map((d, i) => (
                              <div key={`ny-${i}`} className="text-xs sm:text-sm text-gray-800 py-1 min-h-[24px]">
                                {d?.naYin || ""}
                              </div>
                            ))}

                            {/* Row 11: Shen Sha */}
                            <div className="text-xs sm:text-sm font-bold text-gray-500 py-1">神煞</div>
                            {pillars.map((_, i) => <div key={`sha-${i}`} className="py-1 min-h-[24px]"></div>)}
                          </div>
                        );
                      })()}
                    </div>
                  </div>

                  {/* Panel G：全部大运列表 */}
                  <div className="flex-1 bg-white rounded-xl border border-gray-300 shadow-lg overflow-hidden p-3 sm:p-6 w-full min-w-0">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2 sm:mb-4">大运列表</h3>

                    {(!daYunData.daYunArr || daYunData.daYunArr.length === 0) && (
                      <div className="text-sm text-gray-500">未找到大运数据。</div>
                    )}

                    {daYunData.daYunArr && daYunData.daYunArr.length > 0 && (
                      <>
                        {/* 大运行 */}
                        <div className="w-full mb-0 pb-0 sm:mb-3 sm:pb-2">
                          <div className="grid grid-cols-10 w-full gap-0">
                            {Array.from({ length: 10 }).map((_, idx) => {
                              const p = daYunData.daYunArr[idx];
                              const isSelected = selectedDayunIndex === idx;
                              const startYear = p?.start_year || "";
                              const startAge = p?.start_age ?? "—";
                              const endAge = p?.end_age ?? "—";
                              return (
                                <div key={idx} className="w-full">
                                  {p ? (
                                    <button
                                      onClick={() => {
                                        setSelectedDayunIndex(idx);
                                        setSelectedLiunianIndex(0);
                                      }}
                                      className={`w-full flex flex-col text-center py-1 sm:py-2 box-border ${isSelected ? "bg-indigo-100 text-indigo-800" : "text-gray-800"}`}
                                    >
                                      {/* 起始年份 - 固定在顶部 */}
                                      <div className="text-xs mb-0 sm:mb-1 flex-shrink-0">{startYear}</div>

                                      {/* 天干地支 */}
                                      <div className="flex-1 flex flex-col items-center justify-start pt-1 sm:pt-2 overflow-hidden">
                                        {(() => {
                                          const [stem, branch] = parsePillar(p.pillar);
                                          return (
                                            <>
                                              <div className={`text-lg sm:text-xl font-bold leading-tight ${stem ? getStemColor_nobg(stem) : "text-gray-400"}`}>{stem || "—"}</div>
                                              <div className={`text-lg sm:text-xl font-bold leading-tight ${branch ? getBranchColor_nobg(branch) : "text-gray-400"}`}>{branch || "—"}</div>
                                            </>
                                          );
                                        })()}
                                      </div>

                                      {/* 年龄 - 固定在底部 */}
                                      <div className="text-xs mt-1 sm:mt-2 flex-shrink-0">
                                        <div>{startAge} 岁</div>
                                        <div className="text-gray-400 mt-0.5">{endAge} 岁</div>
                                      </div>
                                    </button>
                                  ) : (
                                    <div
                                      className="w-full rounded-lg box-border flex items-center justify-center text-gray-300"
                                    >
                                      —
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>

                        {/* 流年行 */}
                        <div className="w-full pb-0 sm:pb-2">
                          <div className="grid grid-cols-10 w-full gap-0">
                            {(() => {
                              const selDayun = daYunData.daYunArr[selectedDayunIndex] || {};
                              const liList = Array.isArray(selDayun.liunian) ? selDayun.liunian : [];
                              return Array.from({ length: 10 }).map((_, i) => {
                                const ln = liList[i];
                                const isLiSelected = selectedLiunianIndex === i;
                                return (
                                  <div key={i} className="w-full">
                                    {ln ? (
                                      <button
                                        onClick={() => setSelectedLiunianIndex(i)}
                                        className={`w-full flex flex-col text-center py-1 sm:py-2 box-border ${isLiSelected ? "bg-indigo-100 text-indigo-800" : "text-gray-800"}`}
                                      >
                                        {/* 年份 - 固定在顶部 */}
                                        <div className="text-xs mb-0 sm:mb-1 flex-shrink-0">{ln.year}</div>

                                        {/* 天干地支 */}
                                        <div className="flex-1 flex flex-col items-center justify-start pt-1 sm:pt-2 overflow-hidden">
                                          {(() => {
                                            const [stem, branch] = parsePillar(ln.pillar);
                                            return (
                                              <>
                                                <div className={`text-lg sm:text-xl font-bold leading-tight ${stem ? getStemColor_nobg(stem) : "text-gray-400"}`}>{stem || "—"}</div>
                                                <div className={`text-lg sm:text-xl font-bold leading-tight ${branch ? getBranchColor_nobg(branch) : "text-gray-400"}`}>{branch || "—"}</div>
                                              </>
                                            );
                                          })()}
                                        </div>

                                        {/* 年龄 - 紧跟地支后显示 */}
                                        <div className="text-xs mt-1 sm:mt-2 text-gray-500">
                                          {ln.age ?? ""}
                                        </div>
                                      </button>
                                    ) : (
                                      <div
                                        className="w-full rounded-lg box-border flex items-center justify-center text-gray-300"
                                      >
                                        —
                                      </div>
                                    )}
                                  </div>
                                );
                              });
                            })()}
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </>
            )}
          </>
        )}

        {selectedTab === "bazi" && (
          <>
            <div className="bg-white rounded-xl border border-gray-300 shadow-lg overflow-hidden p-3 sm:p-5 w-full min-w-0 mb-4 sm:mb-6">
                  {displayName && (
                    <p className="text-xs text-gray-500 mb-2"> 姓名：{displayName}</p>
                )}
                  <p className="text-xs text-gray-500 mb-2"> 阳历：{form?.year}年{form?.month}月{form?.day}日 {form?.hour}时 | {daYunData?.meta?.previous_jieqi?.name || "上个节气"}：{daYunData?.meta?.previous_jieqi?.datetime || "—"}</p>
                  <p className="text-xs text-gray-500"> 阴历：{lunarBirthText || "—"} | {daYunData?.meta?.next_jieqi?.name || "下个节气"}：{daYunData?.meta?.next_jieqi?.datetime || "—"}</p>
            </div>

            <div className="flex flex-col lg:flex-row gap-4 sm:gap-6 mb-4 sm:mb-6">
              <div className="flex-1 bg-white rounded-xl border border-gray-300 shadow-lg overflow-hidden p-3 sm:p-6 w-full min-w-0">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-4 sm:mb-6">
                  <div>
                    {(form?.zhanti_type || form?.zhanti) && (
                      <p className="text-sm sm:text-base text-gray-600">占类：{form?.zhanti_type || "—"} | {form?.zhanti || "—"}</p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-5 gap-0 text-center items-center">
                  {/* Row 1: Date/Title */}
                  <div className="text-xs sm:text-sm font-bold text-gray-500 pb-2">日期</div>
                  {["年柱", "月柱", "日柱", "时柱"].map((t, i) => (
                    <div key={`t-${i}`} className="text-xs sm:text-sm font-semibold text-gray-500 pb-2">{t}</div>
                  ))}

                  {/* Row 2: Main Star (主星) */}
                  <div className="text-xs sm:text-sm font-bold text-gray-500 py-1">主星</div>
                  {['year', 'month', 'day', 'hour'].map(p => (
                    <div key={`ms-${p}`} className="text-xs sm:text-sm text-gray-800 py-1 min-h-[24px]">
                      {baziAnalysis[p].ganShiShen}
                    </div>
                  ))}

                  {/* Row 3: Stems (天干) */}
                  <div className="text-xs sm:text-sm font-bold text-gray-500 py-1">天干</div>
                  {[result.year_pillar, result.month_pillar, result.day_pillar, result.hour_pillar].map((p, i) => {
                    const [stem] = parsePillar(p);
                    return (
                      <div key={`s-${i}`} className={`text-2xl sm:text-4xl font-bold ${stem ? getStemColor_nobg(stem) : ""}`}>
                        {stem || ""}
                      </div>
                    );
                  })}

                  {/* Row 4: Branches (地支) */}
                  <div className="text-xs sm:text-sm font-bold text-gray-500 py-1">地支</div>
                  {[result.year_pillar, result.month_pillar, result.day_pillar, result.hour_pillar].map((p, i) => {
                    const [, branch] = parsePillar(p);
                    return (
                      <div key={`b-${i}`} className={`text-2xl sm:text-4xl font-bold ${branch ? getBranchColor_nobg(branch) : ""}`}>
                        {branch || ""}
                      </div>
                    );
                  })}

                  {/* Row 5: Hidden Stems & Sub Star (藏干/副星) */}
                  <div className="text-xs sm:text-sm font-bold text-gray-500 py-1 self-start mt-2">藏干</div>
                  {[
                    { hidden: result.year_pillar_hidden_stems, subStars: baziAnalysis.year.zhiShiShen },
                    { hidden: result.month_pillar_hidden_stems, subStars: baziAnalysis.month.zhiShiShen },
                    { hidden: result.day_pillar_hidden_stems, subStars: baziAnalysis.day.zhiShiShen },
                    { hidden: result.hour_pillar_hidden_stems, subStars: baziAnalysis.hour.zhiShiShen }
                  ].map((data, i) => (
                    <div key={`hs-${i}`} className="flex flex-col items-center gap-0 mt-2 self-start min-h-[50px]">
                      {/* 藏干在第一行 */}
                      <div className="flex items-center gap-1 flex-wrap mb-1">
                        {data.hidden && data.hidden.map((h, idx) => (
                          <span key={`h-${idx}`} className={`text-xs font-medium leading-tight ${getStemColor_nobg(h)}`}>
                            {h}
                          </span>
                        ))}
                      </div>
                      {/* 副星在第二行 */}
                      <div className="flex items-center gap-1 flex-wrap">
                        {data.subStars.map((s, idx) => (
                          <span key={`s-${idx}`} className="text-xs text-gray-600 leading-tight">
                            {s}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}

                  {/* Row 7: Star Luck (星运) */}
                  <div className="text-xs sm:text-sm font-bold text-gray-500 py-1">星运</div>
                  {['year', 'month', 'day', 'hour'].map(p => (
                    <div key={`sl-${p}`} className="text-xs sm:text-sm text-gray-800 py-1 min-h-[24px]">
                      {baziAnalysis[p].starLuck}
                    </div>
                  ))}

                  {/* Row 8: Self Sit (自坐) */}
                  <div className="text-xs sm:text-sm font-bold text-gray-500 py-1">自坐</div>
                  {['year', 'month', 'day', 'hour'].map(p => (
                    <div key={`sit-${p}`} className="text-xs sm:text-sm text-gray-800 py-1 min-h-[24px]">
                      {baziAnalysis[p].diShi}
                    </div>
                  ))}

                  {/* Row 9: Void (空亡) */}
                  <div className="text-xs sm:text-sm font-bold text-gray-500 py-1">空亡</div>
                  {['year', 'month', 'day', 'hour'].map(p => (
                    <div key={`kw-${p}`} className="text-xs sm:text-sm text-gray-800 py-1 min-h-[24px]">
                      {baziAnalysis[p].xunKong}
                    </div>
                  ))}

                  {/* Row 10: Na Yin (纳音) */}
                  <div className="text-xs sm:text-sm font-bold text-gray-500 py-1">纳音</div>
                  {['year', 'month', 'day', 'hour'].map(p => (
                    <div key={`ny-${p}`} className="text-xs sm:text-sm text-gray-800 py-1 min-h-[24px]">
                      {baziAnalysis[p].naYin}
                    </div>
                  ))}

                  {/* Row 11: Shen Sha (神煞) - Empty */}
                  <div className="text-xs sm:text-sm font-bold text-gray-500 py-1">神煞</div>
                  {[0, 1, 2, 3].map(i => <div key={`sha-${i}`} className="py-1 min-h-[24px]"></div>)}
                </div>
              </div>

              {/* Unified Tabs Panel */}
              <div className="flex-1 bg-white rounded-xl border border-gray-300 shadow-lg overflow-hidden p-3 sm:p-6 w-full min-w-0">
                <div className="flex gap-2 border-b border-indigo-100 pb-3 mb-4 overflow-x-auto">
                  <button
                    onClick={() => setDetailTab("pillar")}
                    className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
                      detailTab === "pillar"
                        ? "bg-indigo-600 text-white"
                        : "bg-white text-gray-600 border border-gray-200"
                    }`}
                  >
                    四柱判断
                  </button>
                  <button
                    onClick={() => setDetailTab("owner")}
                    className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
                      detailTab === "owner"
                        ? "bg-indigo-600 text-white"
                        : "bg-white text-gray-600 border border-gray-200"
                    }`}
                  >
                    命主反馈
                  </button>
                  <button
                    onClick={() => setDetailTab("master")}
                    className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
                      detailTab === "master"
                        ? "bg-indigo-600 text-white"
                        : "bg-white text-gray-600 border border-gray-200"
                    }`}
                  >
                    命理师记录
                  </button>
                </div>

                {detailTab === "pillar" && (
                  <div className="text-sm sm:text-base text-gray-700 space-y-4">
                    <div className="p-3 bg-white/60 rounded-lg border border-indigo-100 shadow-sm">
                      <h4 className="font-bold text-indigo-800 mb-1">日元旺衰</h4>
                      <p className="text-gray-600 text-sm">（此处显示日元旺衰分析）</p>
                    </div>
                    <div className="p-3 bg-white/60 rounded-lg border border-indigo-100 shadow-sm">
                      <h4 className="font-bold text-indigo-800 mb-1">喜用神建议</h4>
                      <p className="text-gray-600 text-sm">（此处显示喜用神建议）</p>
                    </div>
                    <div className="p-3 bg-white/60 rounded-lg border border-indigo-100 shadow-sm">
                      <h4 className="font-bold text-indigo-800 mb-1">格局分析</h4>
                      <p className="text-gray-600 text-sm">（此处显示格局分析）</p>
                    </div>
                    <div className="p-3 bg-white/60 rounded-lg border border-indigo-100 shadow-sm">
                      <h4 className="font-bold text-indigo-800 mb-1">性格特征</h4>
                      <p className="text-gray-600 text-sm">（此处显示性格特征）</p>
                    </div>
                  </div>
                )}

                {detailTab === "owner" && (
                  <div>
                    <h4 className="text-lg font-semibold mb-2">命主反馈</h4>
                    <textarea
                      value={ownerNote}
                      onChange={(e) => setOwnerNote(e.target.value)}
                      placeholder="请在此填写您的主观感受、补充信息或想要命理师关注的问题"
                      className="w-full min-h-[120px] sm:min-h-[160px] p-3 border border-gray-200 rounded-md text-sm sm:text-base"
                      maxLength={1000}
                    />
                    <div className="text-xs text-gray-500 mt-2 text-right">{ownerNote.length}/1000</div>
                  </div>
                )}

                {detailTab === "master" && (
                  <div>
                    <h4 className="text-lg font-semibold mb-2">命理师记录</h4>
                    <textarea
                      value={masterNote}
                      onChange={(e) => setMasterNote(e.target.value)}
                      placeholder="命理师可在此记录分析要点、建议与后续跟进事项"
                      className="w-full min-h-[120px] sm:min-h-[160px] p-3 border border-gray-200 rounded-md text-sm sm:text-base"
                      maxLength={2000}
                    />
                    <div className="text-xs text-gray-500 mt-2 text-right">{masterNote.length}/2000</div>
                  </div>
                )}

                <div className="mt-4 flex justify-center items-center gap-3 flex-wrap">
                  <button onClick={() => navigate(-1)} className="font-semibold py-2 px-6 rounded-lg bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors">
                    返回
                  </button>
                  <button onClick={handleSaveBazi} disabled={saving} className={`font-semibold py-2 px-6 rounded-lg flex items-center ${saving ? "bg-green-400" : "bg-green-600 hover:bg-green-700 text-white"}`}>
                    {saving ? (<><Loader className="h-5 w-5 mr-2 animate-spin" />保存中...</>) : "保存命盘"}
                  </button>
                  <button onClick={handlePublish} className="font-semibold py-2 px-6 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white transition-colors">
                    发布
                  </button>
                </div>
              </div>
            </div>
          </>
        )}

      </div>

      {/* 发布弹窗 */}
      <BaziPublish
        isOpen={showPublishModal}
        onClose={() => setShowPublishModal(false)}
        onSubmit={handlePublishSubmit}
        chartData={{
          id: form?.chart_id || result?.id,
          content: form?.mingzhu_name,
          type: 'bazi',
          name: form?.mingzhu_name
        }}
      />
    </>
  );
};

export default BaziDetail;